'use client'

import type { UseAccordionItemProps } from '@pearlconvert/ui/components/accordion/accordion.types'

import React, {
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { v4 as uuidV4 } from 'uuid'

import { useEventBusContext } from '@maestro/core/event/event-bus.context'
import { EventType } from '@maestro/data-layer/models/event'
import {
  AccordionActiveIdsContext,
  AccordionAnimationDelay,
  AccordionChangeActiveContext,
  AccordionIsInitial,
} from '@pearlconvert/ui/components/accordion/accordion.context'
import { useHash } from '@pearlconvert/ui/hooks/use-hash'
import { useModals } from '@pearlconvert/ui/providers/modals/modals.context'

export const useAccordionItem = (props: UseAccordionItemProps) => {
  const {
    displayAsDialog,
    itemId,
    hash,
    title,
    ref,
    location = '',
    trackingTitle,
  } = props

  const [itemIdValue] = useState(() => itemId || uuidV4())
  const id = useRef(itemIdValue)
  const activeIds = useContext(AccordionActiveIdsContext)
  const changeActive = useContext(AccordionChangeActiveContext)
  const isInitial = useContext(AccordionIsInitial)
  const animationDelay = useContext(AccordionAnimationDelay)
  const { getHashParamValue, addOrUpdateHashParam, removeHashParam } = useHash()
  const { openModal, closeModal } = useModals()
  const { publish } = useEventBusContext()
  const isActive = activeIds.includes(itemIdValue)
  const [isExpanded, setIsExpanded] = useState(false)
  const headingButtonRef = useRef<HTMLButtonElement>(null)
  let elementId = itemId

  const hasHash = hash !== undefined
  const hashValue = hasHash ? getHashParamValue(hash) : undefined
  const hasHashValue = hashValue !== undefined
  const replaceHash = !displayAsDialog

  const toggle = useCallback(() => {
    if (hasHash) {
      if (isActive) {
        removeHashParam(hash)
      } else {
        addOrUpdateHashParam(hash, '', replaceHash)
      }
      return
    }
    changeActive(itemIdValue)
  }, [
    addOrUpdateHashParam,
    changeActive,
    hasHash,
    hash,
    isActive,
    itemIdValue,
    removeHashParam,
    replaceHash,
  ])

  useEffect(() => {
    if (!isActive) {
      setIsExpanded(false)
      return
    }
    if (animationDelay && isInitial && isActive) {
      const timeout = setTimeout(() => {
        setIsExpanded(true)
        // Focus on the accordion item when it's initially active (opened via activeFacet prop)
        if (isInitial) {
          headingButtonRef.current?.focus()
        }
      }, animationDelay)
      return () => clearTimeout(timeout)
    }

    setIsExpanded(true)
    // Focus on the accordion item when it's initially active (opened via activeFacet prop)
    if (isInitial) {
      headingButtonRef.current?.focus()
    }
  }, [animationDelay, isActive, isInitial])

  useEffect(() => {
    if (!hasHash || hasHashValue === isActive) {
      return
    }
    changeActive(itemIdValue)
  }, [changeActive, hasHash, hasHashValue, isActive, itemIdValue])

  if (!elementId) {
    if (
      React.isValidElement<{ children?: React.ReactNode }>(title) &&
      typeof title.props.children === 'string'
    ) {
      elementId = title.props.children.replaceAll(' ', '&')
    } else if (typeof title === 'string') {
      elementId = title
    }
  }

  useEffect(() => {
    if (!displayAsDialog || hasHash) {
      return
    }
    if (isActive && !hasHash) {
      openModal(id.current)
    } else if (!isActive) {
      closeModal(id.current)
    }
  }, [closeModal, displayAsDialog, hasHash, isActive, openModal])

  const handleToggle = useCallback(() => {
    let titleString: string
    if (trackingTitle) {
      titleString = trackingTitle
    } else if (typeof title === 'string') {
      titleString = title
    } else if (
      React.isValidElement<{ children?: React.ReactNode }>(title) &&
      typeof title.props.children === 'string'
    ) {
      titleString = title.props.children
    } else {
      titleString = ''
    }
    publish(EventType.ACCORDION, {
      accordionTitle: titleString,
      action: isActive ? 'closed' : 'opened',
      location,
    })
    toggle()
  }, [isActive, location, publish, title, trackingTitle, toggle])

  const handleClose = useCallback(() => {
    if (hasHash) {
      removeHashParam(hash)
    } else if (isActive) {
      changeActive(id.current)
    }
    setTimeout(() => {
      headingButtonRef.current?.focus()
    }, 200)
  }, [changeActive, hasHash, hash, isActive, removeHashParam])

  const handleOpen = useCallback(() => {
    if (hasHash) {
      if (!hasHashValue) {
        addOrUpdateHashParam(hash, '', replaceHash)
      } else if (!isActive) {
        changeActive(id.current)
      }
      return
    }
    if (!isActive) {
      changeActive(id.current)
    }
  }, [
    addOrUpdateHashParam,
    changeActive,
    hasHash,
    hasHashValue,
    hash,
    isActive,
    replaceHash,
  ])

  const scrollTitleIntoView = () => {
    const el = headingButtonRef.current
    if (!el) {
      return
    }

    el.style.scrollMarginTop = '120px'
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  useImperativeHandle(
    ref,
    () => ({
      toggle: handleToggle,
      close: handleClose,
      open: handleOpen,
      scrollTitleIntoView,
    }),
    [handleClose, handleOpen, handleToggle]
  )

  return {
    id,
    isExpanded,
    elementId,
    handleClose,
    handleToggle,
    headingButtonRef,
  }
}
